import { useEffect, useLayoutEffect, useState } from "react";

/**
 * Checks whether the tracked element is in the viewport.
 * Re-attaches when `elementRef.current` becomes available after conditional mount.
 *
 * @param {object} elementRef - Tracking target element (React ref object).
 * @param {object} settings - Custom tracking info config.
 * @param {number} settings.threshold - Threshold for intersection (default: 0).
 * @param {Element|null} settings.root - Root element for intersection (default: null).
 * @param {string} settings.rootMargin - Margin around the root (default: "0%").
 * @param {boolean} settings.freezeOnceVisible - Freeze the observer once the element is visible (default: false).
 * @returns {boolean} - `true` if the element is intersecting, `false` otherwise.
 */
function useIntersectionObserver(elementRef, settings = {}) {
  const { threshold = 0, root = null, rootMargin = "0%", freezeOnceVisible = false } = settings;
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [targetNode, setTargetNode] = useState(null);

  useLayoutEffect(() => {
    const node = elementRef?.current ?? null;
    setTargetNode((prevNode) => (prevNode === node ? prevNode : node));
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver || !targetNode) {
      return undefined;
    }

    let observer;

    try {
      const observerParams = { threshold, root, rootMargin };

      /**
       * Updates intersection state from observer entries.
       *
       * @param {Array} entries
       */
      const updateEntry = ([entry]) => {
        if (entry) {
          setIsIntersecting(entry.isIntersecting);
          if (entry.isIntersecting && freezeOnceVisible) {
            observer.unobserve(targetNode);
          }
        }
      };

      observer = new IntersectionObserver(updateEntry, observerParams);
      observer.observe(targetNode);
    } catch (error) {
      console.error("Error setting up IntersectionObserver:", error);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [targetNode, threshold, root, rootMargin, freezeOnceVisible]);

  return isIntersecting;
}

export default useIntersectionObserver;
